import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { ICompany } from '@nxsys/types/models/company';
import { useMemo, useState } from 'react';
import AutoEnrolmentSettings from './components/AutoEnrolmentSettings';
import ContactAndPayment from './components/ContactAndPayment';
import PrivatePensionSchemes from './components/PrivatePensionSchemes';
import EditContactAndPaymentModal from './components/EditContactAndPaymentModal';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import { ContactAndPaymentFormValues } from './components/ContactAndPaymentForm';

type Props = {
  data?: ICompany;
  updateCompany: (params: UpdateCompanyByIdParams) => Promise<void>;
};

enum PensionIrelandTabEnum {
  AUTO_ENROLMENT = 'AUTO_ENROLMENT',
  PRIVATE_PENSIONS = 'PRIVATE_PENSIONS',
  CONTACT_AND_PAYMENT = 'CONTACT_AND_PAYMENT',
}

const PensionIrelandTab: React.FC<Props> = ({ data, updateCompany }) => {
  const { messages } = useTypedIntl();
  const [
    isEditContactAndPaymentModalOpen,
    setIsEditContactAndPaymentModalOpen,
  ] = useState(false);

  const tabs = useMemo(
    () => [
      {
        tab: messages['common.autoEnrolment'],
        key: PensionIrelandTabEnum.AUTO_ENROLMENT,
      },
      {
        tab: messages['common.privatePensions'],
        key: PensionIrelandTabEnum.PRIVATE_PENSIONS,
      },
      {
        tab: messages['common.contactAndPayment'],
        key: PensionIrelandTabEnum.CONTACT_AND_PAYMENT,
      },
    ],
    [messages],
  );

  const [activeTab, setActiveTab] = useState<PensionIrelandTabEnum>(
    PensionIrelandTabEnum.AUTO_ENROLMENT,
  );

  const handleTabChange = (key: PensionIrelandTabEnum) => {
    setActiveTab(key);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case PensionIrelandTabEnum.AUTO_ENROLMENT:
        return <AutoEnrolmentSettings data={data} />;
      case PensionIrelandTabEnum.PRIVATE_PENSIONS:
        return <PrivatePensionSchemes data={data} />;
      case PensionIrelandTabEnum.CONTACT_AND_PAYMENT:
        return (
          <ContactAndPayment
            data={data}
            onEdit={() => setIsEditContactAndPaymentModalOpen(true)}
          />
        );
      default:
        return <></>;
    }
  };

  const handleContactAndPaymentSubmit = async (
    values: ContactAndPaymentFormValues,
  ) => {
    if (!data) return;

    const params: UpdateCompanyByIdParams = {
      pensionSetting: {
        ...data.pensionSetting,
        address: [
          values.address1,
          values.address2,
          values.address3,
          values.address4,
        ].filter(Boolean),
        eirCode: values.eircode || undefined,
        phone: values.phone || undefined,
        bankName: values.bankName || undefined,
        bankBranch: values.bankBranch || undefined,
        bankSortCode: values.bankSortCode || undefined,
        bankAccountNumber: values.accountNumber || undefined,
        bankAccountName: values.bankAccountName || undefined,
        bankReference: values.bankReference || undefined,
        IBAN: values.iban || undefined,
        BIC: values.bic || undefined,
      } as any,
    };
    await updateCompany(params);
    setIsEditContactAndPaymentModalOpen(false);
  };

  return (
    <>
      <AppCardTabListContainer
        tabList={tabs}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        className="!border-none"
      >
        <div className="p-[16px]">{renderTabContent()}</div>
      </AppCardTabListContainer>
      {data && isEditContactAndPaymentModalOpen && (
        <EditContactAndPaymentModal
          isOpen={isEditContactAndPaymentModalOpen}
          toggle={() => setIsEditContactAndPaymentModalOpen(false)}
          onSubmit={handleContactAndPaymentSubmit}
          data={data}
        />
      )}
    </>
  );
};

export default PensionIrelandTab;
